/**
 * Notification Context
 * 
 * Manages a queue of notifications and provides functions to add/remove them
 * Notifications are stacked vertically when displayed
 */

import { createContext, useContext, useReducer, useCallback } from "react";
import PropTypes from "prop-types";

const NotificationContext = createContext(null);

NotificationContext.displayName = "NotificationContext";

// Notification action types
const ADD_NOTIFICATION = "ADD_NOTIFICATION";
const REMOVE_NOTIFICATION = "REMOVE_NOTIFICATION";
const CLEAR_ALL = "CLEAR_ALL";

function notificationReducer(state, action) {
  switch (action.type) {
    case ADD_NOTIFICATION: {
      const newNotification = {
        id: action.payload.id || `notification-${Date.now()}-${Math.random()}`,
        ...action.payload,
      };
      return {
        ...state,
        notifications: [...state.notifications, newNotification],
      };
    }
    case REMOVE_NOTIFICATION: {
      return {
        ...state,
        notifications: state.notifications.filter(
          (n) => n.id !== action.payload.id
        ),
      };
    }
    case CLEAR_ALL: {
      return {
        ...state,
        notifications: [],
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function NotificationProvider({ children }) {
  const [state, dispatch] = useReducer(notificationReducer, {
    notifications: [],
  });

  const addNotification = useCallback((notification) => {
    dispatch({
      type: ADD_NOTIFICATION,
      payload: notification,
    });
  }, []);

  const removeNotification = useCallback((id) => {
    dispatch({
      type: REMOVE_NOTIFICATION,
      payload: { id },
    });
  }, []);

  const clearAllNotifications = useCallback(() => {
    dispatch({
      type: CLEAR_ALL,
    });
  }, []);

  const value = {
    notifications: state.notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// Custom hook for using notification context
function useNotification() {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }

  return context;
}

// Typechecking props for NotificationProvider
NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { NotificationProvider, useNotification };

